import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/motion";
import { ArrowRight, Battery, Car, Factory } from "lucide-react";

const BATTERY_IMG = "https://images.unsplash.com/photo-1591964006776-90b32e88f5ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXRoaXVtJTIwaW9uJTIwYmF0dGVyeSUyMGVuZXJneSUyMHN0b3JhZ2UlMjBzeXN0ZW0lMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc3MTQ4NTkyNnww&ixlib=rb-4.1.0&q=85";
const FACTORY_IMG = "https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxldiUyMGJhdHRlcnklMjBwYWNrJTIwbWFudWZhY3R1cmluZyUyMGhpZ2glMjB0ZWNoJTIwZmFjdG9yeXxlbnwwfHx8fDE3NzE0ODU5Mjh8MA&ixlib=rb-4.1.0&q=85";
const ROBOT_IMG = "https://images.pexels.com/photos/19233057/pexels-photo-19233057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SOLUTIONS = [
  {
    icon: Battery,
    tag: "BESS",
    title: "Battery Energy Storage Systems",
    desc: "Engineered for grid-scale and commercial energy storage — peak shaving, frequency regulation, renewable integration, and backup power. Modular kWh to MWh configurations planned with advanced thermal management and real-time monitoring.",
    specs: ["Modular kWh-MWh", "C-Rate Optimized", "Active Cooling", "Remote Monitoring"],
    image: BATTERY_IMG,
  },
  {
    icon: Car,
    tag: "EV Battery Packs",
    title: "Lithium-Ion Packs for Electric Vehicles",
    desc: "High-energy-density battery packs being designed for 2-wheelers, 3-wheelers, 4-wheelers, and commercial EVs. Each pack architected around specific vehicle dynamics — torque profiles, range requirements, charging protocols, and thermal envelopes.",
    specs: ["2W / 3W / 4W", "Fast Charge Ready", "IP67 Rated", "CAN Protocol BMS"],
    image: FACTORY_IMG,
  },
  {
    icon: Factory,
    tag: "Industrial & Custom",
    title: "Custom Lithium-Ion Solutions",
    desc: "Purpose-built battery systems in development for telecom towers, UPS backup, railway signaling, mining equipment, defense applications, and solar-storage hybrids. Engineered from cell-level upward for each operational environment.",
    specs: ["Telecom / Defense", "Custom Form Factor", "Extended Cycle Life", "Harsh Environment"],
    image: ROBOT_IMG,
  },
];

export default function SolutionsSection() {
  return (
    <section id="solutions" data-testid="solutions-section" className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay overflow-hidden">
      <div className="section-glow absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mb-2"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // Solutions
          </span>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mb-6 md:mb-8"
        >
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 leading-tight">
            <span className="block">Engineered for</span>
            <span className="block lg:whitespace-nowrap text-[#1E96FC]">every energy challenge</span>
          </h2>
        </motion.div>

        {/* Solution Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={staggerContainer}
          className="space-y-6 md:space-y-8"
        >
          {SOLUTIONS.map((sol, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              data-testid={`solution-card-${i}`}
              className="glass-card rounded-sm overflow-hidden group"
            >
              <div className={`grid grid-cols-1 md:grid-cols-12 ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                {/* Image */}
                <div className={`md:col-span-5 relative overflow-hidden h-48 sm:h-56 md:h-auto md:min-h-[280px] ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <img
                    src={sol.image}
                    alt={sol.title}
                    className="w-full h-full md:absolute md:inset-0 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-[#040D1C] via-[#040D1C]/40 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#1E96FC]/10 border border-[#1E96FC]/30 text-white text-xs uppercase tracking-widest rounded-sm">
                      {sol.tag}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className={`md:col-span-7 p-6 md:p-8 lg:p-10 flex flex-col justify-center ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <div className="flex items-center gap-2.5 mb-3 md:mb-4 text-lg md:text-xl lg:text-2xl">
                    <sol.icon className="w-[0.75em] h-[0.75em] text-[#1E96FC] flex-shrink-0" />
                    <h3 className="font-bold uppercase text-slate-100 tracking-wide leading-none">
                      {sol.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-xl">
                    {sol.desc}
                  </p>
                  <div className="flex flex-wrap gap-2 md:gap-3 mb-4 md:mb-6">
                    {sol.specs.map((spec, j) => (
                      <span
                        key={j}
                        className="px-3 py-1.5 bg-[#003771]/30 text-xs text-slate-300 uppercase tracking-wider rounded-sm border border-[#003771]/50"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                  <button
                    data-testid={`solution-learn-more-${i}`}
                    className="inline-flex items-center gap-2 text-sm text-[#1E96FC] font-semibold uppercase tracking-wider group/btn hover:gap-3 transition-all duration-200"
                    onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Register Early Interest
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
